/**
 * priorityQueue.ts
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
 */

export class PriorityQueue<T> {
  values: Array<T>;
  private lessThan: (a: T, b: T) => boolean;

  get length(): number {
    return this.values.length;
  }

  // fn should return true if a is higher priority than b
  constructor (swapIf: (a: T, b: T) => boolean) {
    this.values = [];
    this.lessThan = swapIf;
  }

  empty(): void {
    this.values.splice(0, this.values.length);
  }

  swap (index1: number, index2: number): void {
    const temp = this.values[index1];
    this.values[index1] = this.values[index2];
    this.values[index2] = temp;
  }

  enqueue(value: T): void {
    this.values.push(value);
    this.bubbleUp();
  }

  dequeue(): T | undefined {
    if (this.values.length <= 0) {
      return undefined;
    }
    this.swap(0, this.values.length - 1);
    const popped = this.values.pop();
    this.bubbleDown();
    return popped;
  }

  private bubbleUp (): number {
    let index = this.values.length - 1;

    while (index > 0) {
      const parentIndex = Math.floor((index - 1)/2);

      if (this.lessThan(this.values[index], this.values[parentIndex])) {
        this.swap(index, parentIndex);
        index = parentIndex;
      } else {
        break;
      }
    }
    return 0;
  }

  private bubbleDown (): void {
    let parentIndex = 0;
    const length = this.values.length;
    let indexToSwap: number | null = null;

    while (true) {
      //get indexes of child elements by following formula
      const leftChildIndex = (2 * parentIndex) + 1;
      const rightChildIndex = (2 * parentIndex) + 2;

      // if left child exists, and is greater than the element, plan to swap with the left child index
      if (leftChildIndex < length) {
        if (this.lessThan(this.values[leftChildIndex], this.values[parentIndex])) {
          indexToSwap = leftChildIndex;
        }
      }

      //if right child exists
      if (rightChildIndex < length) {
        if ( // if right child is greater than element and there are
            // no plans to swap
            (this.lessThan(this.values[rightChildIndex],
                this.values[parentIndex]) && indexToSwap == null) ||

            // OR if right child is greater than left child and there
            // ARE plans to swap
            (this.lessThan(this.values[rightChildIndex],
                this.values[leftChildIndex]) && indexToSwap !== null)) {
            //plan to swap with the right child
          indexToSwap = rightChildIndex
        }
      }
      //if there are no plans to swap, break out of the loop
      if (indexToSwap === null) {
        break;
      }

      this.swap(parentIndex, indexToSwap);
      //starting index is now index that we swapped with
      parentIndex = indexToSwap;
      indexToSwap = null;
    }
  }
}
